/**
 * Pure rendering functions for the building-map canvas layers.
 *
 * Each function takes the canvas context, the world transform, and a
 * narrow data slice, and draws one layer. Layers are composited in
 * bottom-to-top order by the parent component. Keeping these as pure
 * functions (no React, no state) makes them easy to test and reason
 * about — the dirty-flag redraw loop just calls them again with the
 * latest data.
 */

import type { DoorState, LiftState, BuildingMap } from '../../api/ws-types';
import type { TrajectorySnapshot, RawKnot } from '../../api/ws-types';
import type { PlaceRegistry } from '../../api/v1';
import type { WorldBBox } from './map-utils';
import type { RobotMark } from './robot-marks';
import type { LiftLook, LiftLookKind } from '../Devices/lift-column-state';
import { sameLevel } from '../../utils/levels';

export interface Camera {
  /** Translation applied to world coords (in canvas pixels). */
  offsetX: number;
  offsetY: number;
  /** Pixels per world unit. */
  scale: number;
  /** Canvas pixel size. */
  width: number;
  height: number;
}

export interface LayerToggles {
  robots: boolean;
  doors: boolean;
  lifts: boolean;
  trajectories: boolean;
  /** No projection feeds this yet — see `VISIBLE_LAYER_KEYS`. Kept in
   *  the type (and `drawNavGraph` kept below) so wiring a nav-graph
   *  projection later is a one-line change here, not a re-write. */
  navGraph: boolean;
  places: boolean;
}

/** Operator-facing names for the layer checkboxes.
 *
 *  The control row used to render `Object.keys(layers)` verbatim, so
 *  the UI literally read "navGraph" and "trajectories" in camelCase —
 *  object keys are an implementation detail and were never meant to be
 *  copy. Exhaustive by type (`Record<keyof LayerToggles, string>`), so
 *  adding a layer without a label is a compile error rather than
 *  another camelCase key leaking to the screen. */
export const LAYER_LABELS: Record<keyof LayerToggles, string> = {
  robots: 'Robots',
  doors: 'Doors',
  lifts: 'Lifts',
  trajectories: 'Paths',
  navGraph: 'Nav graph',
  places: 'Places',
};

/** Which layers actually get a checkbox, in display order.
 *
 *  `navGraph` is deliberately absent. There is NO nav-graph geometry
 *  anywhere in the v1 contract — `BuildingMapSnapshot` carries only
 *  site/level/revision/dimensions/affine/image/walls, and the frontend
 *  adapter therefore hardcodes `nav_graphs: []`. The worker does
 *  consume `/nav_graphs`, but only to derive the place registry, never
 *  as graph geometry. So the toggle could be flipped either way and
 *  nothing would change on screen, which reads as a broken control.
 *
 *  `drawNavGraph` and the `navGraph` field are kept rather than
 *  deleted: making the layer real is a worker projection + v1 schema +
 *  api-server endpoint away, and when that lands the only frontend
 *  change is putting the key back in this array. */
export const VISIBLE_LAYER_KEYS: ReadonlyArray<keyof LayerToggles> = [
  'robots',
  'doors',
  'lifts',
  'trajectories',
  'places',
];

interface MapPalette {
  bg: string;
  grid: string;
  navGraphFill: string;
  movingFill: string;
  robotFill: string;
  robotLabel: string;
  doorStrokeIdle: string;
  doorStrokeOpen: string;
  doorStrokeMoving: string;
  doorStrokeFault: string;
  doorStrokeDefault: string;
  doorStrokeOuter: string;
  textInverse: string;
  /** Wall lines and unlabelled place dots. Were rgba-white literals, so
   *  on the light theme's floorplan they drew white on white. */
  wall: string;
  placeDot: string;
  /** The highlight ring (W4 step 12): the 3D view's selection colour, so
   *  one object is ringed alike in both views. */
  select: string;
}

let _palette: MapPalette | null = null;

function readAllCSSVars(): MapPalette {
  const root = typeof document !== 'undefined' ? document.documentElement : null;
  const css = (name: string, fallback: string): string => {
    if (!root) return fallback;
    const v = getComputedStyle(root).getPropertyValue(name).trim();
    return v || fallback;
  };
  return {
    bg: css('--nx-bg', '#0a0b0d'),
    grid: css('--nx-cyan', '#00e5cc'),
    navGraphFill: css('--nx-blue', '#2e8fff'),
    movingFill: css('--nx-moving', '#ffb547'),
    // One neutral colour for every robot (`robot-marks.ts`); certainty is
    // carried by fill, opacity and dash, never by hue.
    robotFill: css('--nx-scene-robot', '#d5dee6'),
    robotLabel: css('--nx-muted', '#7a8a9a'),
    doorStrokeIdle: css('--nx-cyan', '#00e5cc'),
    doorStrokeOpen: css('--nx-green', '#2ecc71'),
    doorStrokeMoving: css('--nx-moving', '#ffb547'),
    doorStrokeFault: css('--nx-error', '#ff4d6a'),
    doorStrokeDefault: css('--nx-muted', '#7a8a9a'),
    doorStrokeOuter: css('--nx-bg', '#0a0b0d'),
    textInverse: css('--nx-text', '#e8ecf0'),
    wall: css('--nx-map-wall', 'rgba(255, 255, 255, 0.18)'),
    placeDot: css('--nx-map-place', 'rgba(232, 236, 240, 0.42)'),
    select: css('--nx-scene-select', '#c084fc'),
  };
}

if (typeof document !== 'undefined') {
  new MutationObserver(() => {
    _palette = null;
  }).observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
}

/**
 * Theme-aware palette. Reads the current `--nx-*` tokens off the
 * document root so the canvas tracks the active theme. Cached at module
 * scope and invalidated when `data-theme` mutates — the canvas draws
 * at ~60 Hz, so re-reading 11 CSS vars per frame would burn CPU and
 * also pick up stale values mid-frame if a theme switch lands between
 * `getComputedStyle` calls.
 */
export function getMapPalette(): MapPalette {
  return _palette ?? (_palette = readAllCSSVars());
}

export function _resetMapPaletteForTest(): void {
  _palette = null;
}

export const ALL_LAYERS_ON: LayerToggles = {
  robots: true,
  doors: true,
  lifts: true,
  trajectories: true,
  navGraph: true,
  places: true,
};

/** Camera scale (px per world unit) above which robot names are shown.
 *  Below this, the dots are too small and dense for labels to read. */
export const ROBOT_NAME_MIN_SCALE = 8;
/** Minimum visible robot marker radius in pixels. */
const ROBOT_DOT_PX = 6;
/** Shared label font size for both the lift floor label and robot
 *  name label -- kept as one constant so the two stay in sync
 *  (previously 11px/10px respectively; both bumped +2px). */
const LIFT_LABEL_FONT_PX = 13;
const ROBOT_LABEL_FONT_PX = 13;

// World coordinates follow RMF/ROS convention: +Y is physically "up"
// (north). Canvas pixel coordinates increase DOWNWARD (+Y = down the
// screen). Every world-space consumer (robots, doors, lifts, nav
// graph, walls, places, trajectories, and the floorplan image's own
// world-space corners in `drawFloorplan`) is expressed in the former
// and must be flipped to land correctly in the latter — X is
// unaffected. This was missing here (only `offsetY` translated, with
// no sign flip), which every layer silently absorbed by rendering
// vertically mirrored relative to true physical layout; invisible
// with only mutually-consistent synthetic mock data and no ground-
// truth floorplan image to reveal it against. Any code that inverts
// this transform (panning, zoom-to-cursor, click hit-testing,
// fit-to-building) must invert the SAME sign — see `onPointerMove`,
// `onWheel`, `onPointerUp` in `BuildingMapCanvas.tsx` and
// `computeFitToBuilding` below.
export function worldToScreen(cx: number, cy: number, cam: Camera): { x: number; y: number } {
  return {
    x: (cx + cam.offsetX) * cam.scale,
    y: (cam.offsetY - cy) * cam.scale,
  };
}

/**
 * Pan the camera by a pointer delta measured in screen pixels.
 *
 * CONVENTION, pinned by tests: **direct manipulation — you grab the
 * map.** Content tracks the pointer 1:1: drag right and the building
 * moves right under your finger, drag down and it moves down. Both
 * axes obey it, which is the whole reason this is a function rather
 * than two lines in a pointer handler — one place owns the sign, and
 * the test file pins it there.
 *
 * This is what every map tool does (Google Maps, Mapbox, Leaflet,
 * OpenLayers), and it is the only convention an operator dragging a
 * floorplan expects. The alternative — "the VIEW follows the pointer",
 * i.e. a camera that moves right so content slides left — is what this
 * function used to implement, and it read as the map fighting the
 * cursor. Two prior sign slips got there:
 *
 *   1. `onPointerMove` originally had `+dx` (grab-the-map) on X and
 *      `-dy` (camera-follows) on Y, so a diagonal drag moved content
 *      along neither the pointer nor its mirror;
 *   2. the unification that produced this function then collapsed both
 *      axes onto the WRONG one of the two.
 *
 * The trap that motivated the `-dy` was `worldToScreen`'s Y flip. It is
 * a trap because the flip is already fully accounted for once the
 * result is stated in SCREEN space:
 *
 *   screen_x = (world_x + offsetX) * scale  ->  d(screen_x) = d(offsetX) * scale
 *   screen_y = (offsetY - world_y) * scale  ->  d(screen_y) = d(offsetY) * scale
 *
 * Both differentiate to `+d(offset) * scale` — the minus sign in the Y
 * row multiplies `world_y`, which is constant under a pan, not
 * `offsetY`. So wanting d(screen) = +delta on both axes gives
 * `offset += delta / scale` on both, sign of the Y flip
 * notwithstanding. Any other inverse of `worldToScreen` (click
 * hit-testing in `onPointerUp`, zoom-to-cursor in `onWheel`,
 * `computeFitToBuilding`) solves for a world point instead and DOES
 * carry the flip — those are correct as they stand and are not
 * symmetric with this one.
 */
export function panCamera(cam: Camera, dx: number, dy: number): Camera {
  return {
    ...cam,
    offsetX: cam.offsetX + dx / cam.scale,
    offsetY: cam.offsetY + dy / cam.scale,
  };
}

export function clearCanvas(ctx: CanvasRenderingContext2D, cam: Camera): void {
  ctx.fillStyle = getMapPalette().bg;
  ctx.fillRect(0, 0, cam.width, cam.height);
}

export interface FloorplanAffine {
  x_offset: number;
  y_offset: number;
  yaw: number;
  scale: number;
}

/**
 * World-space position of one image-pixel coordinate under the
 * floorplan's affine transform. `(px, py)` is a raster pixel
 * coordinate — py increases DOWNWARD (standard image convention).
 * World coordinates increase Y UPWARD (standard robot/RMF
 * convention), so py must be negated before the rotation is applied,
 * not just scaled. Confirmed empirically against real building-map
 * data: with a real (nonzero, ~0.0085 m/px) `scale` and doors/robots
 * at negative world-Y, the un-flipped formula placed the entire
 * floorplan image in a disjoint positive-Y region from every door
 * and robot — invisible with the mock's near-identity affine values,
 * which never exercised this path meaningfully.
 */
export function imagePixelToWorld(px: number, py: number, a: FloorplanAffine): { x: number; y: number } {
  const cosY = Math.cos(a.yaw);
  const sinY = Math.sin(a.yaw);
  return {
    x: a.x_offset + px * a.scale * cosY + py * a.scale * sinY,
    y: a.y_offset + px * a.scale * sinY - py * a.scale * cosY,
  };
}

/**
 * World-space corners of the floorplan image (top-left, top-right,
 * bottom-left, bottom-right) — shared between `drawFloorplan` (draws
 * it) and the auto-fit bounding-box calculation in
 * `BuildingMapCanvas.tsx` (which needs the image included in "fit to
 * building", not just nav-graph/door/lift points — a floorplan much
 * larger than its few graph points otherwise auto-fits to a tiny
 * fraction of the pane).
 */
export function floorplanWorldCorners(
  affine: FloorplanAffine,
  pixelWidth: number,
  pixelHeight: number,
): { tl: { x: number; y: number }; tr: { x: number; y: number }; bl: { x: number; y: number }; br: { x: number; y: number } } {
  return {
    tl: imagePixelToWorld(0, 0, affine),
    tr: imagePixelToWorld(pixelWidth, 0, affine),
    bl: imagePixelToWorld(0, pixelHeight, affine),
    br: imagePixelToWorld(pixelWidth, pixelHeight, affine),
  };
}

function drawFloorplan(
  ctx: CanvasRenderingContext2D,
  cam: Camera,
  img: HTMLImageElement | null,
  affine: FloorplanAffine,
  pixelWidth: number,
  pixelHeight: number,
): boolean {
  if (!img || !img.complete || img.naturalWidth === 0) return false;
  ctx.save();
  const corners = floorplanWorldCorners(affine, pixelWidth, pixelHeight);
  const tl = worldToScreen(corners.tl.x, corners.tl.y, cam);
  const tr = worldToScreen(corners.tr.x, corners.tr.y, cam);
  const bl = worldToScreen(corners.bl.x, corners.bl.y, cam);
  // Approximate axis-aligned bounding box from the transformed
  // corners; we can't preserve rotation cheaply via canvas drawImage
  // without a temporary canvas, and the mock floorplan images are
  // typically unrotated.
  //
  // `w`/`h` MUST be a proper max-min span across all three corners,
  // not `tr - tl` / `bl - tl` — that assumes `tl` is always the
  // bounding box's min corner, which only held under the un-flipped
  // (pre-Y-fix) formula where increasing pixel py always increased
  // screen y too. With the Y flip above, `bl` can now have a SMALLER
  // screen y than `tl` (bl - tl negative), so `Math.max(negative, 0)`
  // collapsed to the `|| 1` fallback and rendered the floorplan as a
  // 1px sliver — invisible in practice.
  const minX = Math.min(tl.x, tr.x, bl.x);
  const minY = Math.min(tl.y, tr.y, bl.y);
  const w = Math.max(tl.x, tr.x, bl.x) - minX || 1;
  const h = Math.max(tl.y, tr.y, bl.y) - minY || 1;
  ctx.globalAlpha = 0.7;
  ctx.drawImage(img, minX, minY, w, h);
  ctx.globalAlpha = 1;
  ctx.restore();
  return true;
}

/**
 * Wall-graph wireframe fallback. Some upstream building-map payloads
 * (notably the `tinyRobot` sim world) ship `wall_graph` data but no
 * embedded floorplan PNG, so the canvas otherwise paints black with
 * only the nav graph + doors + robots on top. Drawing the wall
 * segments as a low-contrast wireframe gives operators a legible
 * floor outline without competing with the nav graph (cyan) or door
 * lines. Falls through silently if `wall_graph` is also empty —
 * `drawNavGraph` already draws its vertices + edges as the degraded
 * fallback.
 */
function drawWalls(
  ctx: CanvasRenderingContext2D,
  cam: Camera,
  level: BuildingMap['levels'][number] | undefined,
): void {
  if (!level) return;
  const walls = level.wall_graph;
  if (!walls) return;
  const vertices = walls.vertices ?? [];
  const edges = walls.edges ?? [];
  if (vertices.length === 0 || edges.length === 0) return;
  ctx.save();
  ctx.lineWidth = 1;
  // Subtle stroke so the cyan nav graph + door lines stay prominent.
  ctx.strokeStyle = getMapPalette().wall;
  ctx.beginPath();
  for (const edge of edges) {
    const a = vertices[edge.v1_idx];
    const b = vertices[edge.v2_idx];
    if (!a || !b) continue;
    const pa = worldToScreen(a.x, a.y, cam);
    const pb = worldToScreen(b.x, b.y, cam);
    ctx.moveTo(pa.x, pa.y);
    ctx.lineTo(pb.x, pb.y);
  }
  ctx.stroke();
  ctx.restore();
}

function drawNavGraph(
  ctx: CanvasRenderingContext2D,
  cam: Camera,
  level: BuildingMap['levels'][number] | undefined,
): void {
  if (!level) return;
  ctx.save();
  ctx.strokeStyle = 'rgba(0, 229, 204, 0.35)';
  ctx.lineWidth = 1;
  for (const graph of level.nav_graphs ?? []) {
    const nodes = graph.vertices ?? [];
    ctx.beginPath();
    for (const edge of graph.edges ?? []) {
      const a = nodes[edge.v1_idx];
      const b = nodes[edge.v2_idx];
      if (!a || !b) continue;
      const pa = worldToScreen(a.x, a.y, cam);
      const pb = worldToScreen(b.x, b.y, cam);
      ctx.moveTo(pa.x, pa.y);
      ctx.lineTo(pb.x, pb.y);
    }
    ctx.stroke();
  }
  ctx.fillStyle = 'rgba(46, 143, 255, 0.85)';
  for (const n of level.nav_graphs?.flatMap((g) => g.vertices ?? []) ?? []) {
    const p = worldToScreen(n.x, n.y, cam);
    ctx.beginPath();
    ctx.arc(p.x, p.y, 2, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

/** Screen-pixel radius of a cleaning-zone marker.
 *
 *  Deliberately fixed in SCREEN space, not derived from `cam.scale`
 *  like the robot dot is. A zone is a single nav-graph vertex, not an
 *  area — it has no world extent to be faithful to — and its job here
 *  is to be a click target that stays the same size whether the
 *  operator is looking at a whole floor or one corridor. */
const ZONE_MARKER_PX = 7;
/** Screen-pixel radius of an ordinary named waypoint. Small enough to
 *  read as texture (where the robots can go) rather than as content. */
const PLACE_DOT_PX = 2;
/** World-space click tolerance for a cleaning-zone marker, in metres.
 *  Matched to the robot hit radius in `BuildingMapCanvas.hitTest` so
 *  the two branches feel the same under the finger; exported so the
 *  hit-test and this renderer cannot drift apart. */
export const CLEANING_ZONE_HIT_RADIUS_M = 0.6;

const ZONE_MARKER_FILL = 'rgba(0, 229, 204, 0.35)';
const ZONE_MARKER_STROKE = 'rgba(0, 229, 204, 0.95)';

/**
 * Named waypoints for the active level.
 *
 * Two visual classes, and the split is a legibility decision, not a
 * stylistic one. sg-nomad-01 has HUNDREDS of named waypoints across 14
 * floors; labelling all of them paints the floorplan solid with
 * overlapping text at any zoom that shows more than a corridor. So:
 *
 *   - `is_cleaning_zone` places (~4-13 per floor) get a filled,
 *     screen-size-stable marker WITH its name. They are the only
 *     places an operator acts on from this map — clicking one opens a
 *     prefilled Clean dispatch — so they have to be both findable and
 *     identifiable without hovering.
 *   - every other named place gets a small unlabeled dot. It still
 *     answers "can a robot get there", which is the whole value of the
 *     layer, at a fraction of the ink.
 *
 * `places` arrives through `MapRenderInputs`, ALREADY filtered to the
 * active level by the caller — the same seam robots, trajectories and
 * live door/lift states travel. It deliberately does NOT come from
 * `level.places`: the building-map adapter hardcodes `places: []`
 * (the v1 `BuildingMapSnapshot` carries no place list), while the
 * places themselves are served by `GET /api/v1/sites/{site}/places` and
 * reach us via `usePlaces()`. Routing them through the adapter would
 * mean inventing a second fetch inside a pure shape-conversion
 * function.
 */
function drawPlaces(
  ctx: CanvasRenderingContext2D,
  cam: Camera,
  places: readonly PlaceRegistry[] | undefined,
  zoneFills?: ReadonlyMap<string, string> | null,
): void {
  if (!places || places.length === 0) return;
  ctx.save();
  ctx.font = '11px "DM Sans", sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  for (const place of places) {
    // Same world->screen transform (and Y flip) every other layer
    // uses; a place drawn any other way would not line up with the
    // robot standing on it.
    const p = worldToScreen(place.x, place.y, cam);
    if (p.x < -50 || p.y < -50 || p.x > cam.width + 50 || p.y > cam.height + 50) continue;
    if (place.is_cleaning_zone) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, ZONE_MARKER_PX, 0, Math.PI * 2);
      // The cleaned-zone colour when that colouring is on (`zoneFills`),
      // so the marker says the same as the cell around it.
      ctx.fillStyle = zoneFills?.get(place.name) ?? ZONE_MARKER_FILL;
      ctx.fill();
      ctx.strokeStyle = ZONE_MARKER_STROKE;
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.fillStyle = getMapPalette().textInverse;
      ctx.fillText(place.name, p.x + ZONE_MARKER_PX + 4, p.y);
    } else {
      ctx.beginPath();
      ctx.arc(p.x, p.y, PLACE_DOT_PX, 0, Math.PI * 2);
      ctx.fillStyle = getMapPalette().placeDot;
      ctx.fill();
    }
  }
  ctx.restore();
}

/** One charging dock as the map draws it: where, and the words beside it
 *  (`dockWords`, already worded for the reader by the caller). */
export interface DockMark {
  readonly x: number;
  readonly y: number;
  readonly label: string;
}

/** Half the side of the dock badge, in screen pixels. Fixed in screen
 *  space like a cleaning-zone marker: a dock is one waypoint, with no world
 *  extent to be faithful to, and it has to stay findable at fit scale. */
export const DOCK_BADGE_PX = 9;

/** The bolt inside the badge, as fractions of `DOCK_BADGE_PX` (screen y
 *  down). Exported so a test can find the glyph by its first point. */
export const DOCK_BOLT: ReadonlyArray<readonly [number, number]> = [
  [0.2, -0.75],
  [-0.45, 0.12],
  [-0.02, 0.12],
  [-0.2, 0.75],
  [0.45, -0.12],
  [0.02, -0.12],
];

/**
 * The charging docks on this floor (`dock-places.ts`): a square badge with
 * a lightning bolt, and the dock's words beside it.
 *
 * Square, because every other mark on this map is round -- robots, zones,
 * waypoints -- and no other mark is square. Neutral colours only (the
 * background as a plate, the text colour for the outline and bolt): every
 * hue on this canvas already means something (cyan structure, blue nav
 * graph, amber motion, green/red door states, violet selection), and the
 * shape is what carries "dock". Drawn with the places, under the doors,
 * lifts and robots, so a robot sitting on its dock is drawn over it.
 */
function drawDocks(
  ctx: CanvasRenderingContext2D,
  cam: Camera,
  docks: readonly DockMark[] | undefined,
): void {
  if (!docks || docks.length === 0) return;
  const palette = getMapPalette();
  const s = DOCK_BADGE_PX;
  ctx.save();
  ctx.setLineDash([]);
  ctx.font = '600 11px "DM Sans", sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  for (const dock of docks) {
    // The robots' transform, Y flip included: a dock drawn any other way
    // would not sit under the robot parked on it.
    const p = worldToScreen(dock.x, dock.y, cam);
    if (p.x < -50 || p.y < -50 || p.x > cam.width + 50 || p.y > cam.height + 50) continue;
    ctx.beginPath();
    ctx.moveTo(p.x - s, p.y - s);
    ctx.lineTo(p.x + s, p.y - s);
    ctx.lineTo(p.x + s, p.y + s);
    ctx.lineTo(p.x - s, p.y + s);
    ctx.closePath();
    ctx.fillStyle = palette.bg;
    ctx.fill();
    ctx.strokeStyle = palette.textInverse;
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.beginPath();
    DOCK_BOLT.forEach(([bx, by], i) => {
      const x = p.x + bx * s;
      const y = p.y + by * s;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.closePath();
    ctx.fillStyle = palette.textInverse;
    ctx.fill();
    ctx.fillText(dock.label, p.x + s + 5, p.y);
  }
  ctx.restore();
}

export function doorColor(state: string | undefined): string {
  const p = getMapPalette();
  switch (state) {
    case 'open':
      return p.doorStrokeOpen;
    case 'closed':
      return p.doorStrokeFault;
    case 'moving':
      return p.doorStrokeMoving;
    case 'fault':
      return p.doorStrokeFault;
    default:
      return p.doorStrokeDefault;
  }
}

/** Brighter than `--nx-moving` on purpose — this is the "on" half of
 *  a door/lift-door's flash while it's mid-transit, meant to jump out
 *  of the map at a glance, not just tint the same as the static
 *  amber every other "moving" indicator (StatusChip, etc.) uses. */
/** The bright half of a moving door's flash. Exported because the twin
 *  flashes on the same 400 ms beat with the same colour -- an operator
 *  reads one building on two surfaces, so the states must not look
 *  different between them. */
export const DOOR_FLASH_COLOR = '#fff45e';
const DOOR_FLASH_GLOW = 'rgba(255, 244, 94, 0.85)';
/** How often a moving door alternates, in ms. Shared with the twin. */
export const DOOR_FLASH_PERIOD_MS = 400;

/** Moving doors/lift-doors alternate between the normal amber stroke
 *  and a brighter flash colour (plus a glow + thicker line on the
 *  "on" beat) — a static colour alone didn't read as "something is
 *  actively happening right now" on a map glanced at from a
 *  distance, which is the whole point of flagging motion here. */
function applyDoorStrokeStyle(ctx: CanvasRenderingContext2D, state: string | undefined, flashOn: boolean): void {
  const isMoving = state === 'moving';
  if (isMoving && flashOn) {
    ctx.strokeStyle = DOOR_FLASH_COLOR;
    ctx.lineWidth = 6;
    ctx.shadowColor = DOOR_FLASH_GLOW;
    ctx.shadowBlur = 14;
  } else {
    ctx.strokeStyle = doorColor(state);
    ctx.lineWidth = 4;
    ctx.shadowBlur = 0;
  }
}

/** Open doors render as a dotted line (panel out of the way, less
 *  visual weight); closed/moving/fault render solid. */
function doorLineDash(state: string | undefined): number[] {
  return state === 'open' ? [2, 5] : [];
}

function drawDoors(
  ctx: CanvasRenderingContext2D,
  cam: Camera,
  level: BuildingMap['levels'][number] | undefined,
  liveDoorStates: Record<string, DoorState | undefined> | undefined,
  flashOn: boolean,
): void {
  if (!level) return;
  ctx.save();
  ctx.lineCap = 'round';
  for (const door of level.doors ?? []) {
    const state = liveDoorStates?.[door.name];
    const mode = (state as { current_mode?: { value?: number } } | undefined)?.current_mode?.value;
    let resolvedState: string | undefined;
    if (typeof mode === 'number') {
      resolvedState = mode === 1 ? 'open' : mode === 2 ? 'moving' : mode === 3 ? 'fault' : 'closed';
    } else if ((state as { state?: string } | undefined)?.state) {
      resolvedState = (state as { state?: string }).state;
    }
    const isOpen = resolvedState === 'open';
    applyDoorStrokeStyle(ctx, resolvedState, flashOn);
    ctx.setLineDash(doorLineDash(resolvedState));
    const v1 = worldToScreen(door.v1_x, door.v1_y, cam);
    const v2 = worldToScreen(door.v2_x, door.v2_y, cam);
    ctx.beginPath();
    ctx.moveTo(v1.x, v1.y);
    ctx.lineTo(v2.x, v2.y);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.shadowBlur = 0;

    // Swing arc indicator: a dashed 90°-quadrant drawn from the
    // first vertex using the door's wall segment as the radius.
    // This makes the door's hinge and swing direction legible at
    // a glance. Hidden when the door is fully open (the panel is
    // out of the way and the swing line would just add noise).
    if (!isOpen) {
      const dx = v2.x - v1.x;
      const dy = v2.y - v1.y;
      const r = Math.hypot(dx, dy);
      const startAngle = Math.atan2(dy, dx);
      ctx.save();
      ctx.setLineDash([3, 3]);
      ctx.lineWidth = 1;
      ctx.globalAlpha = 0.55;
      ctx.beginPath();
      ctx.arc(v1.x, v1.y, r, startAngle, startAngle + Math.PI / 2);
      ctx.stroke();
      ctx.restore();
    }
  }
  ctx.restore();
}

/** The fills a solid car may have. Green only for a live car standing on
 *  the floor being viewed ("walk right in"); a translucent blue anywhere
 *  else it is drawn (reachable from here, the car on another floor, or a
 *  reading that is not live). */
const LIFT_FILL_HERE = 'rgba(46, 204, 113, 0.55)';
const LIFT_FILL_ELSEWHERE = 'rgba(46, 143, 255, 0.18)';
/** A dimmed car (quiet, checking, off) is drawn at this opacity. */
const LIFT_DIM_ALPHA = 0.5;
/** A hollow car's dashed edge. */
const LIFT_HOLLOW_DASH = [4, 4];

/**
 * The car itself: a box, and nothing inside it. No chevrons and no
 * centreline: a car never gets a direction, and a dashed "moving" line
 * read as one (twin slice 1, W6 step 13).
 *
 *   solid   live: filled, green only on the viewed floor, the edge in
 *           the car's motion colour;
 *   dim     quiet, checking or off: the same box at half opacity, never
 *           green, in the idle colour;
 *   hollow  lost or absent: a dashed edge in the muted tone and no body,
 *           because nothing says where the car is.
 */
function drawLiftPictogram(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  halfW: number,
  halfH: number,
  look: LiftLookKind,
  stroke: string,
  onViewedFloor: boolean,
): void {
  // Lift car body — inset slightly from the bounding box so the
  // pictogram reads as a labelled object rather than just a fill.
  const inset = 2;
  const left = cx - halfW + inset;
  const top = cy - halfH + inset;
  const width = Math.max(1, halfW * 2 - inset * 2);
  const height = Math.max(1, halfH * 2 - inset * 2);
  ctx.save();
  ctx.lineWidth = 2;
  if (look === 'hollow') {
    ctx.strokeStyle = getMapPalette().doorStrokeDefault;
    ctx.setLineDash(LIFT_HOLLOW_DASH);
    ctx.strokeRect(left, top, width, height);
    ctx.setLineDash([]);
  } else {
    if (look === 'dim') ctx.globalAlpha = LIFT_DIM_ALPHA;
    ctx.fillStyle = look === 'solid' && onViewedFloor ? LIFT_FILL_HERE : LIFT_FILL_ELSEWHERE;
    ctx.fillRect(left, top, width, height);
    ctx.strokeStyle = stroke;
    ctx.strokeRect(left, top, width, height);
  }
  ctx.restore();
}

/** What a car with no look is drawn as: absent, a hollow box. */
const NO_LIFT_LOOK: LiftLook = { look: 'hollow', level: null, door: null, motion: null };

function drawLifts(
  ctx: CanvasRenderingContext2D,
  cam: Camera,
  buildingMap: BuildingMap | undefined,
  liftLooks: ReadonlyMap<string, LiftLook> | undefined,
  activeLevelName: string,
  flashOn: boolean,
): void {
  if (!buildingMap) return;
  ctx.save();
  for (const lift of buildingMap.lifts ?? []) {
    if (!lift.levels?.includes(activeLevelName)) continue;
    // What this car may honestly be drawn as (`useLiftColumnState`, W6):
    // its freshness on this device's receipts, the floor its own row
    // names, and its door and motion only while live. A car the caller
    // gave no look is drawn as absent -- never from the raw row, whose
    // floor, door and motion can be any age.
    const look = liftLooks?.get(lift.name) ?? NO_LIFT_LOOK;
    const palette = getMapPalette();
    let stroke = palette.doorStrokeIdle;
    if (look.motion === 'moving') stroke = palette.doorStrokeMoving;
    else if (look.motion === 'fault') stroke = palette.doorStrokeFault;
    // Every lift drawn here already services `activeLevelName` (see
    // the `continue` above), but that only means the lift is
    // reachable from this floor -- its cabin can only physically be
    // on ONE floor at a time. `onViewedFloor` distinguishes "parked
    // here right now, walk right in" from "accessible from here, but
    // the cabin itself is elsewhere"; the pictogram turns it green only
    // for a live car.
    const onViewedFloor = look.level !== null && look.level === activeLevelName;
    // Bounding box centered at (ref_x, ref_y). `ref_yaw` used to be
    // ignored entirely here (box always drawn axis-aligned), which
    // happened to look right for a lift whose real orientation is
    // ~0/180° and visibly wrong for any other orientation — the
    // pictogram (and by extension its car-door side) didn't line up
    // with the registry-derived landing door drawn by `drawDoors`,
    // which HAS always used the door's own real yaw. Rotating the
    // box by `-ref_yaw` (sign flipped for the screen's flipped-Y
    // convention, same reasoning as `worldToScreen`) fixes that.
    const cx = lift.ref_x;
    const cy = lift.ref_y;
    const halfW = (lift.width ?? 1) / 2;
    const halfD = (lift.depth ?? 1) / 2;
    const mid = worldToScreen(cx, cy, cam);
    const boxW = halfW * 2 * cam.scale;
    const boxH = halfD * 2 * cam.scale;
    const yaw = lift.ref_yaw ?? 0;
    ctx.save();
    ctx.translate(mid.x, mid.y);
    ctx.rotate(-yaw);
    // Draw the elevator pictogram inside the (now-rotated) bounding box.
    drawLiftPictogram(ctx, 0, 0, boxW / 2, boxH / 2, look.look, stroke, onViewedFloor);
    ctx.restore();

    // The cabin's own landing door(s) (`Lift.doors[]` upstream,
    // separate from -- and not to be confused with -- a `Level`'s
    // own `doors[]`; see `rmf_bridge.py::lift_registries_from_map`).
    // Drawn at their real, absolute world position/orientation, NOT
    // inside the rotated pictogram transform above: this geometry is
    // already correctly oriented in world space, same as `drawDoors`'s
    // level doors. Colored/dashed the same way (plan §Phase 4D "lift
    // door orientation") by the lift's own reported `door_state` — a
    // lift has one door state for the whole cabin, not one per leaf.
    // Only a live car has one here; any other is drawn as unknown.
    const door = look.door ?? undefined;
    for (const d of lift.doors ?? []) {
      ctx.save();
      ctx.lineCap = 'round';
      applyDoorStrokeStyle(ctx, door, flashOn);
      ctx.setLineDash(doorLineDash(door));
      const v1 = worldToScreen(d.v1_x, d.v1_y, cam);
      const v2 = worldToScreen(d.v2_x, d.v2_y, cam);
      ctx.beginPath();
      ctx.moveTo(v1.x, v1.y);
      ctx.lineTo(v2.x, v2.y);
      ctx.stroke();
      ctx.restore();
    }

    // Floor name only (no "Lift " prefix -- the pictogram itself
    // already reads as a lift), centered on the pictogram rather than
    // underneath it. The floor the car's own row names, or nothing: a
    // lost or absent car gets no floor, and the first served level is
    // never put in its place (it read as "the car is at L1").
    if (look.level !== null && look.look !== 'hollow') {
      ctx.save();
      if (look.look === 'dim') ctx.globalAlpha = LIFT_DIM_ALPHA;
      ctx.fillStyle = palette.textInverse;
      ctx.font = `${LIFT_LABEL_FONT_PX}px "DM Sans", sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(look.level, mid.x, mid.y);
      ctx.restore();
    }
  }
  ctx.restore();
}

const ROBOT_COLORS = [
  '#2e8fff',
  '#00e5cc',
  '#ffb547',
  '#ff4d6a',
  '#a78bfa',
  '#34d399',
];

/** A stable colour per fleet. Now used only by the paths layer's
 *  no-clock fallback; robot marks are one neutral colour. */
function fleetColor(fleet: string | undefined, _fleetIndex: number): string {
  // Stable hash → index → colour. Avoids collision only if same fleet.
  const fallback = ROBOT_COLORS[0] ?? '#2e8fff';
  if (!fleet) return fallback;
  let h = 0;
  for (let i = 0; i < fleet.length; i++) h = (h * 31 + fleet.charCodeAt(i)) >>> 0;
  return ROBOT_COLORS[h % ROBOT_COLORS.length] ?? fallback;
}

/**
 * Where a heading tick of length `len` ends, in SCREEN pixels, for a
 * robot at screen point `p` facing `yaw` (world radians, counter-
 * clockwise from +X, RMF convention).
 *
 * The Y term is SUBTRACTED. `worldToScreen` flips Y (world +Y is up the
 * screen), so a world direction (cos, sin) is (cos, -sin) on screen.
 * This used to add it, which mirrored every heading top-to-bottom: a
 * robot facing north (yaw pi/2) drew its tick pointing DOWN the screen,
 * and the 2D and 3D views disagreed about which way a robot faced.
 */
export function headingTip(
  p: { x: number; y: number },
  yaw: number,
  len: number,
): { x: number; y: number } {
  return { x: p.x + Math.cos(yaw) * len, y: p.y - Math.sin(yaw) * len };
}

/**
 * The robots, as `robot-marks.ts` decided to draw them. Nothing here
 * decides WHETHER or WHERE a robot is drawn -- that is the marks list,
 * shared with the hit test -- only how each look is painted.
 */
function drawRobots(
  ctx: CanvasRenderingContext2D,
  cam: Camera,
  marks: readonly RobotMark[] | undefined,
): void {
  if (!marks || marks.length === 0) return;
  ctx.save();
  // Marker radius and heading-tick length scale gently with the
  // camera zoom so the robot stays readable at both fit-to-building
  // and max-zoom levels. We cap the upper bound so the dot doesn't
  // obscure an entire floor at extreme zoom.
  const radius = Math.min(14, Math.max(ROBOT_DOT_PX, cam.scale * 0.6));
  const headLen = Math.min(20, Math.max(8, cam.scale));
  const showNames = cam.scale > ROBOT_NAME_MIN_SCALE;
  const palette = getMapPalette();
  for (const m of marks) {
    const p = worldToScreen(m.x, m.y, cam);
    if (p.x < -20 || p.y < -20 || p.x > cam.width + 20 || p.y > cam.height + 20) continue;
    ctx.globalAlpha = m.style.opacity;
    if (m.style.hollow) {
      // The ghost: where the robot was last really seen. Hollow and
      // dashed, so it can never be read as a robot standing there now.
      ctx.setLineDash([...(m.style.dash ?? [])]);
      ctx.strokeStyle = palette.robotFill;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
    } else {
      ctx.fillStyle = palette.robotFill;
      ctx.beginPath();
      ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
      ctx.fill();
      // Heading tick, only while live, so a fleet of identical dots
      // still reads as oriented. Drawn in the halo colour so it reads
      // across the neutral dot as well as beyond it: a tick in the
      // dot's own colour was invisible inside the dot.
      if (m.style.heading && m.yaw !== null) {
        const tip = headingTip(p, m.yaw, headLen);
        ctx.strokeStyle = palette.doorStrokeOuter;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(tip.x, tip.y);
        ctx.stroke();
      }
      ctx.strokeStyle = palette.doorStrokeOuter;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
      ctx.stroke();
    }
    if (showNames) {
      ctx.fillStyle = palette.textInverse;
      ctx.font = `${ROBOT_LABEL_FONT_PX}px "DM Sans", sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      ctx.fillText(m.name, p.x + 8, p.y - 4);
    }
  }
  ctx.globalAlpha = 1;
  ctx.restore();
}

interface PathStrokeStyle {
  width: number;
  alpha: number;
  dash: number[];
  cap: CanvasLineCap;
  glow: number;
}

// This map is a "where's it headed next" tool for staff, not an audit
// trail — so the emphasis is inverted from the conventional
// done-vs-planned cartography convention: already-traversed history
// fades into the background (thin, dotted, muted) so the eye goes
// straight to the bright, thick, glowing upcoming path, which is the
// actionable information. The upcoming style also pulses (two
// alternating variants, driven by `flashOn`) using the same 400ms
// flash cadence `BuildingMapCanvas.tsx` already drives for moving
// doors/lifts, so a robot with real motion ahead reads as "live" at a
// glance the same way an opening door does.
const TRAVERSED_STYLE: PathStrokeStyle = { width: 1.4, alpha: 0.3, dash: [0.1, 9], cap: 'round', glow: 0 };
const UPCOMING_STYLE_DIM: PathStrokeStyle = { width: 4, alpha: 0.88, dash: [], cap: 'round', glow: 6 };
const UPCOMING_STYLE_BRIGHT: PathStrokeStyle = { width: 5, alpha: 1, dash: [], cap: 'round', glow: 14 };
const FALLBACK_STYLE: PathStrokeStyle = { width: 2, alpha: 0.6, dash: [], cap: 'butt', glow: 0 };

function strokeWorldPolyline(
  ctx: CanvasRenderingContext2D,
  cam: Camera,
  points: ReadonlyArray<readonly [number, number]>,
  colour: string,
  style: PathStrokeStyle,
): void {
  if (points.length < 2) return;
  ctx.strokeStyle = colour;
  ctx.lineWidth = style.width;
  ctx.lineCap = style.cap;
  ctx.globalAlpha = style.alpha;
  ctx.setLineDash(style.dash);
  if (style.glow > 0) {
    ctx.shadowColor = colour;
    ctx.shadowBlur = style.glow;
  }
  ctx.beginPath();
  for (let i = 0; i < points.length; i++) {
    const pt = points[i];
    if (!pt) continue;
    const p = worldToScreen(pt[0], pt[1], cam);
    if (i === 0) ctx.moveTo(p.x, p.y);
    else ctx.lineTo(p.x, p.y);
  }
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.shadowBlur = 0;
  ctx.globalAlpha = 1;
  ctx.lineCap = 'butt';
}

/** Arrowhead at the far end of the upcoming path, oriented along the
 *  final segment — the clearest possible "this is where it's going"
 *  cue, independent of how far staff have zoomed out. */
function drawDestinationArrow(
  ctx: CanvasRenderingContext2D,
  cam: Camera,
  from: readonly [number, number],
  to: readonly [number, number],
  colour: string,
  glow: number,
): void {
  const tip = worldToScreen(to[0], to[1], cam);
  const tail = worldToScreen(from[0], from[1], cam);
  const sdx = tip.x - tail.x;
  const sdy = tip.y - tail.y;
  const slen = Math.hypot(sdx, sdy);
  if (slen < 1e-6) return;
  const arrowLenPx = 12;
  const arrowWidthPx = 8;
  const sux = sdx / slen;
  const suy = sdy / slen;
  const perpX = -suy;
  const perpY = sux;

  ctx.save();
  ctx.fillStyle = colour;
  ctx.globalAlpha = 1;
  ctx.shadowColor = colour;
  ctx.shadowBlur = glow;
  ctx.beginPath();
  ctx.moveTo(tip.x, tip.y);
  ctx.lineTo(tip.x - sux * arrowLenPx + perpX * arrowWidthPx / 2, tip.y - suy * arrowLenPx + perpY * arrowWidthPx / 2);
  ctx.lineTo(tip.x - sux * arrowLenPx - perpX * arrowWidthPx / 2, tip.y - suy * arrowLenPx - perpY * arrowWidthPx / 2);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

interface TrajectoryLeg {
  /** Index into `segments`, inclusive at both ends. Consecutive legs
   *  share their boundary index (leg[i].endIdx === leg[i+1].startIdx). */
  startIdx: number;
  endIdx: number;
  hue: number;
  lightness: number;
}

// A robot slows to ~0 at each named waypoint/place it's actually
// stopping at (RMF's own planned-hold behaviour), which makes ground
// speed a reasonable proxy for "leg boundary" without the backend
// needing to expose place names or task-phase structure - this is a
// purely geometric, client-side heuristic, so it costs nothing beyond
// the render pass that was already walking these points.
//
// Confirmed live: this also catches incidental multi-second holds that
// aren't a task's named place (door waits, lane-approach holds), so a
// two-place patrol can render as more than two shades. That's a known,
// accepted tradeoff (still correctly reuses one shade per physical
// stop, still shows real sequence) rather than a bug - getting exactly
// one shade per named place would need the backend to expose
// task/place semantics per segment, not just geometry.
const STOP_SPEED_THRESHOLD = 0.05; // m/s
// Waypoints/places closer than this to an already-seen stop on the
// SAME trajectory are treated as the same physical place and reuse its
// shade, so a patrol loop (lobby -> shop -> lobby -> shop -> ...) reads
// as two shades, not an ever-growing rainbow.
const STOP_REUSE_RADIUS_M = 1.5;
// Two fixed hues only (blue, then teal/green) - hue does NOT move
// between adjacent sections, only lightness does, within a family. A
// prior version continuously swept hue between sections, which still
// read as an unpleasant colour shift leg-to-leg even confined to a
// narrow arc (confirmed live). This is a stricter reading of "vary the
// shade, not the colour": within a family, lightness steps light to
// dark and back (so adjacent legs are always a visibly different
// shade); hue only advances to the next family once a full lightness
// cycle is used up, so most consecutive legs share the exact same hue.
const SECTION_HUES = [208, 165]; // blue, teal/green
const SECTION_LIGHTNESS_STEPS = [46, 58, 70]; // dark -> light, cycles

function sectionShade(index: number): { hue: number; lightness: number } {
  const familyIdx = Math.floor(index / SECTION_LIGHTNESS_STEPS.length) % SECTION_HUES.length;
  const hue = SECTION_HUES[familyIdx]!;
  const lightness = SECTION_LIGHTNESS_STEPS[index % SECTION_LIGHTNESS_STEPS.length]!;
  return { hue, lightness };
}

function computeLegs(segs: RawKnot[]): TrajectoryLeg[] {
  const stopIdxs: number[] = [];
  let runStart = -1;
  for (let i = 0; i < segs.length; i++) {
    const v = segs[i]!.v;
    const stopped = Math.hypot(v[0], v[1]) < STOP_SPEED_THRESHOLD;
    if (stopped && runStart === -1) runStart = i;
    if (!stopped && runStart !== -1) {
      stopIdxs.push(Math.floor((runStart + i - 1) / 2));
      runStart = -1;
    }
  }
  if (runStart !== -1) stopIdxs.push(Math.floor((runStart + segs.length - 1) / 2));

  // Only INTERNAL stops count as leg boundaries — a stop at index 0
  // (trajectory's own starting position) isn't an "arrival" at anything.
  const internal = stopIdxs.filter((i) => i > 0 && i < segs.length - 1);
  const boundaries = [0, ...internal, segs.length - 1].filter(
    (b, i, arr) => i === 0 || b !== arr[i - 1],
  );

  const knownStops: Array<{ x: number; y: number; hue: number; lightness: number }> = [];
  let nextIndex = 0;
  const legs: TrajectoryLeg[] = [];
  for (let i = 0; i < boundaries.length - 1; i++) {
    const startIdx = boundaries[i]!;
    const endIdx = boundaries[i + 1]!;
    const dest = segs[endIdx]!.x;
    const existing = knownStops.find(
      (s) => Math.hypot(s.x - dest[0], s.y - dest[1]) < STOP_REUSE_RADIUS_M,
    );
    let hue: number;
    let lightness: number;
    if (existing) {
      ({ hue, lightness } = existing);
    } else {
      ({ hue, lightness } = sectionShade(nextIndex));
      nextIndex++;
      knownStops.push({ x: dest[0], y: dest[1], hue, lightness });
    }
    legs.push({ startIdx, endIdx, hue, lightness });
  }
  return legs;
}

function legColour(hue: number, lightness: number): string {
  return `hsl(${hue.toFixed(1)}, 62%, ${lightness.toFixed(1)}%)`;
}

/** Traversed/upcoming split + stroke for one leg's index range, given
 *  the trajectory's global "now" split index (or -1/segs.length for
 *  "leg entirely upcoming/behind"). Returns the leg's own local
 *  crossing point (world coords) when "now" falls inside it, so the
 *  caller can place the single anchor dot without recomputing it. */
function drawLeg(
  ctx: CanvasRenderingContext2D,
  cam: Camera,
  segs: RawKnot[],
  leg: TrajectoryLeg,
  nowMs: number,
  upcomingStyle: PathStrokeStyle,
): readonly [number, number] | null {
  const colour = legColour(leg.hue, leg.lightness);
  const legPoints: Array<readonly [number, number]> = [];
  for (let i = leg.startIdx; i <= leg.endIdx; i++) legPoints.push([segs[i]!.x[0], segs[i]!.x[1]]);

  const localSplit = segs
    .slice(leg.startIdx, leg.endIdx + 1)
    .findIndex((s) => s.t > nowMs);

  if (localSplit === -1) {
    // Whole leg already behind the robot.
    strokeWorldPolyline(ctx, cam, legPoints, colour, TRAVERSED_STYLE);
    return null;
  }
  if (localSplit === 0) {
    // Whole leg still ahead.
    strokeWorldPolyline(ctx, cam, legPoints, colour, upcomingStyle);
    return null;
  }

  const before = segs[leg.startIdx + localSplit - 1]!;
  const after = segs[leg.startIdx + localSplit]!;
  const span = after.t - before.t;
  const frac = span > 0 ? (nowMs - before.t) / span : 0;
  const crossing: readonly [number, number] = [
    before.x[0] + (after.x[0] - before.x[0]) * frac,
    before.x[1] + (after.x[1] - before.x[1]) * frac,
  ];

  strokeWorldPolyline(
    ctx, cam, [...legPoints.slice(0, localSplit), crossing], colour, TRAVERSED_STYLE,
  );
  strokeWorldPolyline(
    ctx, cam, [crossing, ...legPoints.slice(localSplit)], colour, upcomingStyle,
  );
  return crossing;
}

function drawTrajectories(
  ctx: CanvasRenderingContext2D,
  cam: Camera,
  snapshot: TrajectorySnapshot | undefined,
  flashOn: boolean,
): void {
  if (!snapshot) return;
  ctx.save();
  const nowMs = snapshot.now_ms;
  const upcomingStyle = flashOn ? UPCOMING_STYLE_BRIGHT : UPCOMING_STYLE_DIM;

  for (const traj of snapshot.trajectories ?? []) {
    const segs = traj.segments ?? [];
    if (segs.length < 2) continue;

    if (nowMs === undefined) {
      // No same-domain clock reference (older publisher) — draw
      // uniformly rather than guess a split point, and skip section
      // colouring too (it's a nice-to-have layered on top, not worth
      // guessing at without a reliable "now").
      const points: Array<readonly [number, number]> = segs.map((s) => [s.x[0], s.x[1]] as const);
      strokeWorldPolyline(ctx, cam, points, fleetColor(traj.fleet_name, 0), FALLBACK_STYLE);
      continue;
    }

    const legs = computeLegs(segs);
    let anchor: readonly [number, number] | null = null;
    for (const leg of legs) {
      const crossing = drawLeg(ctx, cam, segs, leg, nowMs, upcomingStyle);
      if (crossing) anchor = crossing;
    }

    const lastLeg = legs[legs.length - 1];
    const stillHasUpcoming = segs[segs.length - 1]!.t > nowMs;
    if (lastLeg && stillHasUpcoming) {
      const last = segs[lastLeg.endIdx]!.x;
      const prevIdx = Math.max(lastLeg.startIdx, lastLeg.endIdx - 1);
      const prev = segs[prevIdx]!.x;
      drawDestinationArrow(
        ctx, cam, [prev[0], prev[1]], [last[0], last[1]],
        legColour(lastLeg.hue, lastLeg.lightness), upcomingStyle.glow,
      );
    }

    if (anchor) {
      // Small anchor dot at the robot's current position along the
      // plan, tying the faded-past and bright-upcoming halves
      // together. Coloured by whichever leg "now" actually falls in.
      const owningLeg = legs.find((l) => {
        const t0 = segs[l.startIdx]!.t;
        const t1 = segs[l.endIdx]!.t;
        return nowMs >= t0 && nowMs <= t1;
      });
      const anchorLeg = owningLeg ?? legs[0];
      const colour = legColour(
        anchorLeg?.hue ?? SECTION_HUES[0]!, anchorLeg?.lightness ?? SECTION_LIGHTNESS_STEPS[0]!,
      );
      const p = worldToScreen(anchor[0], anchor[1], cam);
      ctx.beginPath();
      ctx.fillStyle = colour;
      ctx.globalAlpha = 1;
      ctx.shadowColor = colour;
      ctx.shadowBlur = upcomingStyle.glow;
      ctx.arc(p.x, p.y, 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.globalAlpha = 1;
    }
  }
  ctx.restore();
}

// ---------------------------------------------------------------------------
// The highlight ring (twin slice 1, plan W4 step 12)
// ---------------------------------------------------------------------------

/**
 * What the map is asked to point at: the ONE highlight prop both renderers
 * take (contract C0 §7), `{kind, id}` spelled as the locate contract spells
 * a focus -- a robot by `<fleet>/<robot>`, a lift, door or zone by name.
 * `level` (the floor itself) and any other kind ring nothing.
 */
export interface MapHighlight {
  readonly kind: string;
  readonly id: string;
}

/**
 * Where the ring went on THIS floor's map:
 *
 *   ringed         the object is drawn here, and ringed;
 *   car-elsewhere  a lift that serves this floor, whose car reports another
 *                  floor: the shaft here is ringed, the car is not claimed;
 *   no-floor       a lift whose car reports no floor (lost, or no reading):
 *                  the shaft here is ringed and NO floor is claimed for the
 *                  car -- the page says so in words;
 *   absent         nothing to ring on this floor's map: another floor, or
 *                  not drawn at all. What the page says is the resolver's
 *                  answer, not the map's.
 */
export type MapHighlightState = 'ringed' | 'car-elsewhere' | 'no-floor' | 'absent';

export interface MapHighlightPlan {
  readonly state: MapHighlightState;
  /** The ring, in world units: its centre, and the radius of what it goes
   *  round (0 for a point). Null when nothing is ringed. */
  readonly ring: { readonly x: number; readonly y: number; readonly radius: number } | null;
}

export interface MapHighlightInputs {
  readonly highlight: MapHighlight | null | undefined;
  /** This floor's robot marks: exactly the list the robots were drawn
   *  from, so a ring is never put where no robot is drawn. */
  readonly robotMarks: readonly RobotMark[] | undefined;
  readonly buildingMap: BuildingMap | undefined;
  readonly activeLevel: BuildingMap['levels'][number] | undefined;
  readonly activeLevelName: string;
  /** This floor's places, as drawn. */
  readonly places: readonly PlaceRegistry[] | undefined;
  /** Each car's look: where its own row says it is, or no floor. */
  readonly liftLooks: ReadonlyMap<string, LiftLook> | undefined;
}

const ABSENT: MapHighlightPlan = Object.freeze({ state: 'absent', ring: null });

/**
 * Pure: where the highlight goes on this floor's map. It reads only what
 * the map draws -- the marks, the floor's doors and places, the lifts that
 * serve the floor and each car's look -- so a ring can never stand where
 * nothing is drawn, and a lift is never given a floor its car did not
 * report.
 */
export function mapHighlightPlan(inputs: MapHighlightInputs): MapHighlightPlan | null {
  const h = inputs.highlight;
  if (!h || !h.id) return null;
  switch (h.kind) {
    case 'robot': {
      const mark = inputs.robotMarks?.find((m) => m.key === h.id);
      return mark ? { state: 'ringed', ring: { x: mark.x, y: mark.y, radius: 0 } } : ABSENT;
    }
    case 'lift': {
      const lift = (inputs.buildingMap?.lifts ?? []).find((l) => l.name === h.id);
      if (!lift || !lift.levels?.includes(inputs.activeLevelName)) return ABSENT;
      const ring = {
        x: lift.ref_x,
        y: lift.ref_y,
        radius: Math.hypot((lift.width ?? 1) / 2, (lift.depth ?? 1) / 2),
      };
      const look = inputs.liftLooks?.get(lift.name);
      // No look is a car nothing reports: the map draws it hollow, with no
      // floor (`drawLifts`), and so does this.
      if (!look || look.level === null || look.look === 'hollow') return { state: 'no-floor', ring };
      return {
        state: sameLevel(look.level, inputs.activeLevelName) ? 'ringed' : 'car-elsewhere',
        ring,
      };
    }
    case 'door': {
      const door = (inputs.activeLevel?.doors ?? []).find((d) => d.name === h.id);
      if (!door) return ABSENT;
      return {
        state: 'ringed',
        ring: {
          x: (door.v1_x + door.v2_x) / 2,
          y: (door.v1_y + door.v2_y) / 2,
          radius: Math.hypot(door.v2_x - door.v1_x, door.v2_y - door.v1_y) / 2,
        },
      };
    }
    case 'zone': {
      const place = (inputs.places ?? []).find((p) => p.name === h.id);
      return place ? { state: 'ringed', ring: { x: place.x, y: place.y, radius: 0 } } : ABSENT;
    }
    default:
      return null;
  }
}

/** The ring is never smaller than this on screen, or a point object at
 *  fit-to-building scale is ringed by a circle a finger covers. */
export const HIGHLIGHT_MIN_RADIUS_PX = 20;
/** Clearance between the object's own edge and the ring, in px. */
const HIGHLIGHT_PAD_PX = 8;

/** The ring's radius on screen, in px, at this camera. */
export function highlightRadiusPx(worldRadius: number, cam: Camera): number {
  return Math.max(HIGHLIGHT_MIN_RADIUS_PX, worldRadius * cam.scale + HIGHLIGHT_PAD_PX);
}

/**
 * The ring itself: a circle in the selection colour over a halo in the
 * background colour, so it reads over a floorplan, a robot or a lit car.
 * Nothing inside it is filled, so what it rings stays readable, and it is
 * drawn last, over every layer. It takes no tap.
 */
export function drawHighlight(
  ctx: CanvasRenderingContext2D,
  cam: Camera,
  plan: MapHighlightPlan | null | undefined,
): void {
  if (!plan?.ring) return;
  const p = worldToScreen(plan.ring.x, plan.ring.y, cam);
  const r = highlightRadiusPx(plan.ring.radius, cam);
  const palette = getMapPalette();
  ctx.save();
  ctx.setLineDash([]);
  ctx.strokeStyle = palette.bg;
  ctx.lineWidth = 6;
  ctx.beginPath();
  ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
  ctx.stroke();
  ctx.strokeStyle = palette.select;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

export interface FloorplanImage {
  html: HTMLImageElement | null;
  pixelWidth: number;
  pixelHeight: number;
  affine: { x_offset: number; y_offset: number; yaw: number; scale: number };
}

export interface MapRenderInputs {
  ctx: CanvasRenderingContext2D;
  cam: Camera;
  buildingMap: BuildingMap | undefined;
  activeLevel: BuildingMap['levels'][number] | undefined;
  activeLevelName: string;
  floorplan: FloorplanImage | null;
  /** The robots to draw on `activeLevelName`, already decided by
   *  `robotMarksForLevel` (presence, ghosts, the clamp, the simulated
   *  filter). The hit test is handed the SAME list. */
  robotMarks: readonly RobotMark[] | undefined;
  trajectories: TrajectorySnapshot | undefined;
  liveDoorStates: Record<string, DoorState | undefined> | undefined;
  liveLiftStates: Record<string, LiftState | undefined> | undefined;
  /** What each lift car may be drawn as, by name (`useLiftColumnState`
   *  or `useLiftCarViews`, W6). A car without one is drawn as absent:
   *  hollow, with no floor. */
  liftLooks?: ReadonlyMap<string, LiftLook>;
  /** Named waypoints for `activeLevelName`, ALREADY level-filtered by
   *  the caller (`BuildingMapCanvas`), from `usePlaces()`. Lives here
   *  rather than on `activeLevel` because the v1 building-map snapshot
   *  has no place list — see `drawPlaces`. */
  places: readonly PlaceRegistry[] | undefined;
  /** The charging docks on `activeLevelName`, already level-filtered and
   *  worded by the caller (`docksOnLevel`, `dockWords`). Drawn with the
   *  Places layer; absent or empty draws none. */
  docks?: readonly DockMark[];
  layers: LayerToggles;
  /** Current half of the flash cycle — see `BuildingMapCanvas.tsx`'s
   *  flash-driver effect, which only runs the timer (and thus toggles
   *  this) while at least one door/lift-door is `moving` or a robot
   *  has an untraversed trajectory segment ahead. */
  flashOn: boolean;
  /** Where the highlight ring goes (`mapHighlightPlan`); drawn last. */
  highlight?: MapHighlightPlan | null;
  /** The cleaned-zone corridor cells for this floor (`MapCleanedZones`),
   *  a raster whose pixel (0, 0) is world (minX, maxY). Drawn over the
   *  floor plan and under every mark. Absent: none. Not a layer toggle --
   *  it is switched in Preferences. */
  zoneCells?: {
    readonly canvas: CanvasImageSource;
    readonly bounds: { minX: number; maxX: number; minY: number; maxY: number };
  } | null;
  /** Each cleaning-zone marker's fill while that colouring is on. */
  zoneFills?: ReadonlyMap<string, string> | null;
}

/** The cleaned-zone cells, laid on the floor in world coordinates. The
 *  raster's rows run from maxY down, which is the direction `worldToScreen`
 *  flips Y into, so the box maps corner to corner with no flip. */
export function drawZoneCells(
  ctx: CanvasRenderingContext2D,
  cam: Camera,
  cells: MapRenderInputs['zoneCells'],
): void {
  if (!cells) return;
  const { minX, maxX, minY, maxY } = cells.bounds;
  const tl = worldToScreen(minX, maxY, cam);
  const br = worldToScreen(maxX, minY, cam);
  ctx.save();
  ctx.imageSmoothingEnabled = true;
  ctx.drawImage(cells.canvas, tl.x, tl.y, br.x - tl.x, br.y - tl.y);
  ctx.restore();
}

export function renderMap(inputs: MapRenderInputs): void {
  const { ctx, cam, activeLevel, activeLevelName, floorplan, robotMarks, trajectories, liveDoorStates, liftLooks, places, docks, layers, buildingMap, flashOn, highlight, zoneCells, zoneFills } = inputs;
  clearCanvas(ctx, cam);

  // 1) Floorplan image (or wall-graph wireframe fallback when absent).
  const drewFloorplan = floorplan
    ? drawFloorplan(ctx, cam, floorplan.html, floorplan.affine, floorplan.pixelWidth, floorplan.pixelHeight)
    : false;
  if (!drewFloorplan) {
    drawWalls(ctx, cam, activeLevel);
  }

  // 1b) Cleaned-zone cells, on the floor and under everything else.
  drawZoneCells(ctx, cam, zoneCells);

  // 2) Nav graph. No projection feeds this today (see
  //    `VISIBLE_LAYER_KEYS`), so the toggle is not offered and this is
  //    dormant — kept wired so a future nav-graph projection needs no
  //    change here.
  if (layers.navGraph) drawNavGraph(ctx, cam, activeLevel);

  // 3) Place markers (cleaning zones labelled, other waypoints as dots).
  if (layers.places) drawPlaces(ctx, cam, places, zoneFills);

  // 3b) Charging docks, over the waypoint dots and under everything that
  //     moves, so a robot parked on its dock is drawn over the badge.
  if (layers.places) drawDocks(ctx, cam, docks);

  // 4) Doors.
  if (layers.doors) drawDoors(ctx, cam, activeLevel, liveDoorStates, flashOn);

  // 5) Lifts.
  if (layers.lifts) drawLifts(ctx, cam, buildingMap, liftLooks, activeLevelName, flashOn);

  // 6) Robots.
  if (layers.robots) drawRobots(ctx, cam, robotMarks);

  // 7) Trajectories.
  if (layers.trajectories) drawTrajectories(ctx, cam, trajectories, flashOn);

  // 8) The highlight ring, over everything: it points, it never hides.
  drawHighlight(ctx, cam, highlight);
}

export function computeFitToBuilding(bb: WorldBBox, width: number, height: number, padding = 40): Camera {
  const spanX = Math.max(1, bb.maxX - bb.minX);
  const spanY = Math.max(1, bb.maxY - bb.minY);
  const scale = Math.min((width - padding * 2) / spanX, (height - padding * 2) / spanY);
  const cx = (bb.minX + bb.maxX) / 2;
  const cy = (bb.minY + bb.maxY) / 2;
  // Place world center at canvas center. X: (cx + offsetX) * scale =
  // width/2. Y follows `worldToScreen`'s flipped convention:
  // (offsetY - cy) * scale = height/2.
  const offsetX = width / 2 / scale - cx;
  const offsetY = height / 2 / scale + cy;
  return { offsetX, offsetY, scale, width, height };
}